import { number, NumberSchema, object, string, boolean } from 'yup';

import { intl } from '../../lib/translation/TranslationProvider';
import { makeValidateSync } from '../../lib/form/utils';
import { validationMessages } from '../../modules/localization/validationMessages';
import { calcDateTime, getDaysInMonth, isAfterDateTime } from '../../lib/date/date';

export type GetProfileValidationSchemaParams = {
  membershipMinAge: number;
  membershipMaxAge: number;
  isExplicitTermsAcceptanceRequired: boolean;
};

export const getSchema = (params: GetProfileValidationSchemaParams) => {
  const { membershipMinAge, membershipMaxAge, isExplicitTermsAcceptanceRequired } = params;

  const now = new Date();
  const maxDobDate = calcDateTime(now, 'sub', membershipMinAge, 'year');
  const maxPossibleYOB = calcDateTime(now, 'sub', membershipMinAge, 'year').getFullYear();
  const minPossibleYOB = calcDateTime(now, 'sub', membershipMaxAge, 'year').getFullYear();

  const birthdateErrorMessage = intl.formatMessage(validationMessages['validation:form:birthday:required']);
  const birthdateMinAgeErrorMessage = intl.formatMessage(validationMessages['validation:form:birthdate:min-age'], {
    minAge: membershipMinAge,
  });

  const emailErrorMessage = intl.formatMessage(validationMessages['validation:form:email:required']);

  return {
    schema: object({
      firstName: string().required(intl.formatMessage(validationMessages['validation:form:first-name:required'])),
      lastName: string().required(intl.formatMessage(validationMessages['validation:form:last-name:required'])),
      email: string().email(emailErrorMessage).required(emailErrorMessage),
      termsAccepted: isExplicitTermsAcceptanceRequired ? boolean().isTrue().required() : boolean().optional(),
      birthdate: object({
        month: number().required(birthdateErrorMessage).min(1, birthdateErrorMessage).max(12, birthdateErrorMessage),
        date: number()
          .required(birthdateErrorMessage)
          .min(1, birthdateErrorMessage)
          .max(31, birthdateErrorMessage)
          // @ts-ignore Based on docs: https://github.com/jquense/yup#schemawhenkeys-string--string-builder-object--values-any-schema--schema-schema
          // TODO handle ts-ignore when version updated
          .when(['month', 'year'], ([month, year]: [number, number], schema: NumberSchema) => {
            const selectedDate = new Date();
            if (month) {
              selectedDate.setMonth(month - 1);

              if (year) {
                selectedDate.setFullYear(year);
              }

              const daysInMonth = getDaysInMonth(selectedDate);
              return schema.max(daysInMonth, birthdateErrorMessage);
            }
            return schema.max(31, birthdateErrorMessage);
          }),
        year: number()
          .required(birthdateErrorMessage)
          .min(minPossibleYOB, birthdateErrorMessage)
          .max(maxPossibleYOB, birthdateErrorMessage),
        // TODO: refactor
        birthdateError: string().when(
          ['date', 'month', 'year'],
          // @ts-ignore Based on docs: https://github.com/jquense/yup#schemawhenkeys-string--string-builder-object--values-any-schema--schema-schema
          // TODO handle ts-ignore when version updated
          ([date, month, year]: [number, number, number], schema: NumberSchema) => {
            if (date && month && year) {
              const selectedDate = new Date(year, month - 1, date);
              if (!isAfterDateTime(maxDobDate, selectedDate)) {
                return schema.required(birthdateMinAgeErrorMessage);
              }
            }
            return schema;
          },
        ),
      }).required(),
      phoneNumber: string().required(),
    }).required(),
    maxPossibleYOB,
    minPossibleYOB,
  };
};

export const getProfileFormValidationSchema = (params: GetProfileValidationSchemaParams) => {
  const { schema, maxPossibleYOB, minPossibleYOB } = getSchema(params);
  return {
    validationSchema: makeValidateSync(schema),
    maxPossibleYOB,
    minPossibleYOB,
  };
};
